import React, { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { ClerkProvider, useClerk, SignedIn, SignedOut, RedirectToSignIn } from "@clerk/clerk-react";
import ManualQuoteInterface from "./components/ManualQuoteInterface";
import AIQuoteInterface from "./components/AIQuoteInterface";
import DashboardSettings from "./components/DashboardSettings";
import DashboardDemo from "./components/DashboardDemo";
import ProtectedRoute from "./components/ProtectedRoute";
import ErrorBoundary from "./components/ErrorBoundary";
import APITestPage from "./components/APITestPage";
import { Toaster } from "./components/ui/toaster";

const clerkPubKey = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;
const isSatellite = process.env.REACT_APP_CLERK_IS_SATELLITE === 'true';
// console.log('isSatellite', isSatellite);
const clerkDomain = process.env.REACT_APP_CLERK_DOMAIN;
const linkDomain = process.env.REACT_APP_CLERK_LINK_DOMAIN;
const signInUrl = process.env.REACT_APP_CLERK_SIGN_IN_URL;
const signUpUrl = process.env.REACT_APP_CLERK_SIGN_UP_URL;

// Add this component to force auth sync on cross-domain navigation
function AuthSyncHandler() {
  const clerk = useClerk();
  const location = useLocation();

  useEffect(() => {
    // Check if we just arrived from a cross-domain redirect
    const hasClerkParams = 
      window.location.search.includes('__clerk') || 
      window.location.hash.includes('__clerk');
    
    // Check if referrer is from different domain
    let hasCrossDomainReferrer = false;
    if (document.referrer) {
      try {
        const referrerUrl = new URL(document.referrer);
        hasCrossDomainReferrer = referrerUrl.hostname !== window.location.hostname;
      } catch (e) {
        // Invalid referrer
      }
    }

    // If coming from cross-domain, force Clerk to reload session
    if (hasClerkParams || hasCrossDomainReferrer) {
      // console.log('[AuthSync] 🔄 Cross-domain navigation detected, forcing Clerk reload');
      
      // ✅ THIS IS THE KEY - Force Clerk to reload session from server
      // Safely check if the method exists before calling
      if (clerk && typeof clerk.load === 'function') {
        clerk.load({ skipCache: true })
          .then(() => {
            // console.log('[AuthSync] ✅ Clerk reloaded, session:', clerk.session ? 'active' : 'none');
            // console.log('[AuthSync] User:', clerk.user ? clerk.user.id : 'none');
          })
          .catch(error => {
            console.error('[AuthSync] ❌ Failed to reload Clerk:', error);
            // Don't crash - just log the error and let polling handle it
          });
      } else {
        // Method doesn't exist, log and let polling handle it
        // console.log('[AuthSync] ⚠️ clerk.load() not available, relying on polling mechanism');
        // console.log('[AuthSync] Session status:', clerk.session ? 'active' : 'none');
      }
    }
  }, [location, clerk]);

  return null;
}

function App() {
  return (
    <ErrorBoundary>
      <ClerkProvider 
        publishableKey={clerkPubKey}
        {...(isSatellite ? {
          isSatellite: true,
          domain: clerkDomain,
          // linkDomain: linkDomain,
          signInUrl: signInUrl,
          signUpUrl: signUpUrl
        } : {})}
        // allowedRedirectOrigins={['https://studioiq.app', 'http://localhost:3000']}
        // allowedRedirectOrigins={['https://studioiq.co', 'https://studioiq.app' ]}
      >
        <div className="App">
          <BrowserRouter>
            <AuthSyncHandler />
            <Routes>
              <Route path="/" element={<AIQuoteInterface />} />
              <Route path="/manual" element={<ManualQuoteInterface />} />
              <Route path="/dashboard" element={<ProtectedRoute />} />
              <Route path="/demo" element={<DashboardDemo />} />
              <Route path="/api-test" element={<APITestPage />} />
              <Route path="*" element={
                <div className="min-h-screen bg-slate-50 flex items-center justify-center">
                  <div className="text-center">
                    <h1 className="text-2xl font-bold text-slate-900 mb-4">
                      Page Not Found
                    </h1>
                    <p className="text-slate-600 mb-4">
                      The page you're looking for doesn't exist.
                    </p>
                    <a 
                      href="/" 
                      className="bg-slate-900 text-white px-6 py-2 rounded-lg hover:bg-slate-800 transition-colors"
                    >
                      Go Home
                    </a>
                  </div>
                </div>
              } />
            </Routes>
            <Toaster />
          </BrowserRouter>
        </div>
      </ClerkProvider>
    </ErrorBoundary>
  );
}

export default App;